import {
  AccessPattern,
  ApiFramework,
  AuthMechanism,
  AWSResourceUsed,
  Compliance,
  DataHandling,
  DataSensitivity,
  Emerging,
  Impersonation,
  IsolationModel,
  LifecyclePhase,
  Observability,
  OperationalPattern,
  OperationType,
  ResiliencePattern,
  ServicePlane,
  Visibility,
} from '@amzn/helianthus-arbiter-client';
import type { ServiceProperties } from '@amzn/helianthus-arbiter-client';

export type ServicePropertyKey = keyof ServiceProperties;

/**
 * Every service property, in the order the model declares them. Typed as a complete Record so adding a
 * property to the Smithy model breaks the build here instead of silently disappearing from the console.
 */
/** label for display, values are the closed vocabulary straight from the model enum. */
export const SERVICE_PROPERTIES: Record<ServicePropertyKey, { label: string; values: string[] }> = {
  servicePlane: { label: 'Service plane', values: Object.values(ServicePlane) },
  visibility: { label: 'Visibility', values: Object.values(Visibility) },
  apiFramework: { label: 'API framework', values: Object.values(ApiFramework) },
  authMechanism: { label: 'Auth mechanism', values: Object.values(AuthMechanism) },
  awsResourceUsed: { label: 'AWS resource used', values: Object.values(AWSResourceUsed) },
  dataSensitivity: { label: 'Data sensitivity', values: Object.values(DataSensitivity) },
  isolationModel: { label: 'Isolation model', values: Object.values(IsolationModel) },
  lifecyclePhase: { label: 'Lifecycle phase', values: Object.values(LifecyclePhase) },
  impersonation: { label: 'Impersonation', values: Object.values(Impersonation) },
  compliance: { label: 'Compliance', values: Object.values(Compliance) },
  emerging: { label: 'Emerging', values: Object.values(Emerging) },
  operationType: { label: 'Operation type', values: Object.values(OperationType) },
  dataHandling: { label: 'Data handling', values: Object.values(DataHandling) },
  accessPattern: { label: 'Access pattern', values: Object.values(AccessPattern) },
  operationalPattern: { label: 'Operational pattern', values: Object.values(OperationalPattern) },
  resiliencePattern: { label: 'Resilience pattern', values: Object.values(ResiliencePattern) },
  observability: { label: 'Observability', values: Object.values(Observability) },
};

export const SERVICE_PROPERTY_KEYS = Object.keys(SERVICE_PROPERTIES) as ServicePropertyKey[];

/** Values set on a rule, keyed the same way, with empty and missing treated alike. */
export type ServicePropertyValues = Partial<Record<ServicePropertyKey, string[]>>;

export function servicePropertyValues(properties: ServiceProperties | undefined): ServicePropertyValues {
  const values: ServicePropertyValues = {};
  for (const key of SERVICE_PROPERTY_KEYS) {
    const selected = properties?.[key];
    if (selected?.length) values[key] = [...selected];
  }
  return values;
}

/** Drops empty selections so an unset property stays absent rather than becoming an empty list. */
export function toServiceProperties(values: ServicePropertyValues): ServiceProperties {
  return Object.fromEntries(Object.entries(values).filter(([, v]) => v?.length)) as ServiceProperties;
}
